import { useCallback, useEffect, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { FiAlertCircle, FiCheckCircle, FiFileText, FiRefreshCw } from 'react-icons/fi';
import { billingAPI } from '../../../api/billing';
import type { BillingAddress, BillingDetails, BillingDetailsUpdateRequest } from '../../../api/billing';
import { BILLING_COUNTRY_OPTIONS } from '../../../constants/billingCountries';
import { STRIPE_TAX_ID_TYPES } from '../../../constants/stripeTaxIdTypes';
import { useToast } from '../../../hooks/useToast';
import { logger } from '../../../utils/logger';
import { Button } from '../../ui/Button/Button';
import { Card } from '../../ui/Card/Card';
import styles from './BillingDetailsForm.module.css';

interface BillingDetailsFormValues {
  billing_email: string;
  company_name: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  tax_id_type: string;
  tax_id_value: string;
}

export interface BillingDetailsFormProps {
  organizationId: string;
  organizationName?: string;
  canEdit?: boolean;
}

const EMPTY_FORM_VALUES: BillingDetailsFormValues = {
  billing_email: '',
  company_name: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  postal_code: '',
  country: '',
  tax_id_type: '',
  tax_id_value: '',
};

function toText(value?: string | null): string {
  return value || '';
}

function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed || null;
}

function addressFromForm(form: BillingDetailsFormValues): BillingAddress {
  return {
    line1: emptyToNull(form.line1),
    line2: emptyToNull(form.line2),
    city: emptyToNull(form.city),
    state: emptyToNull(form.state),
    postal_code: emptyToNull(form.postal_code),
    country: emptyToNull(form.country)?.toUpperCase() || null,
  };
}

function formFromDetails(details: BillingDetails): BillingDetailsFormValues {
  const address = details.billing_address || {};
  return {
    billing_email: toText(details.billing_email),
    company_name: toText(details.company_name),
    line1: toText(address.line1),
    line2: toText(address.line2),
    city: toText(address.city),
    state: toText(address.state),
    postal_code: toText(address.postal_code),
    country: toText(address.country).toUpperCase(),
    tax_id_type: toText(details.tax_id_type),
    tax_id_value: toText(details.tax_id_value),
  };
}

function getSyncBadgeClass(status?: string): string {
  if (status === 'synced') return `${styles.syncBadge} ${styles.syncBadgeSynced}`;
  if (status === 'failed') return `${styles.syncBadge} ${styles.syncBadgeFailed}`;
  if (status === 'deferred') return `${styles.syncBadge} ${styles.syncBadgeDeferred}`;
  return `${styles.syncBadge} ${styles.syncBadgeUnknown}`;
}

export function BillingDetailsForm({ organizationId, organizationName, canEdit = true }: BillingDetailsFormProps) {
  const toast = useToast();
  const [details, setDetails] = useState<BillingDetails | null>(null);
  const [form, setForm] = useState<BillingDetailsFormValues>(EMPTY_FORM_VALUES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const loadDetails = useCallback(async () => {
    if (!organizationId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await billingAPI.getBillingDetails(organizationId);
      setDetails(response);
      setForm(formFromDetails(response));
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load billing details';
      logger.error('Failed to load billing details:', message);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    void loadDetails();
  }, [loadDetails]);

  const updateField = (field: keyof BillingDetailsFormValues) => (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    setValidationError(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canEdit || !organizationId) return;

    const hasTaxType = Boolean(form.tax_id_type.trim());
    const hasTaxValue = Boolean(form.tax_id_value.trim());
    if (hasTaxType !== hasTaxValue) {
      setValidationError('Tax ID type and value must both be set, or both be empty.');
      return;
    }

    const payload: BillingDetailsUpdateRequest = {
      billing_email: emptyToNull(form.billing_email),
      company_name: emptyToNull(form.company_name),
      billing_address: addressFromForm(form),
      tax_id_type: emptyToNull(form.tax_id_type),
      tax_id_value: emptyToNull(form.tax_id_value),
    };

    try {
      setSaving(true);
      setError(null);
      const response = await billingAPI.updateBillingDetails(organizationId, payload);
      setDetails(response);
      setForm(formFromDetails(response));

      if (response.stripe_sync_status === 'failed') {
        toast.warning('Billing details saved', response.stripe_sync_error || 'Invoice sync failed and will need attention.');
      } else {
        toast.success('Billing details saved', 'Invoice details were updated successfully.');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save billing details';
      logger.error('Failed to save billing details:', message);
      setError(message);
      toast.error('Could not save billing details', message);
    } finally {
      setSaving(false);
    }
  };

  const disabled = loading || saving || !canEdit;
  const syncStatus = details?.stripe_sync_status || 'unknown';
  const showSyncStatus = syncStatus !== 'unknown';

  return (
    <Card className={styles.formCard}>
      <div className={styles.header}>
        <div className={styles.titleGroup}>
          <FiFileText className={styles.headerIcon} />
          <div>
            <h2>Invoice billing details</h2>
            <p>Set the business identity shown on invoices and receipts.</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={() => void loadDetails()} disabled={loading || saving}>
          <FiRefreshCw />
          Reload
        </Button>
      </div>

      {organizationName && <p className={styles.organizationNote}>Organization: {organizationName}</p>}

      {!canEdit && (
        <div className={styles.notice}>
          <FiAlertCircle />
          <span>Only organization owners and admins can edit invoice billing details.</span>
        </div>
      )}

      {error && (
        <div className={styles.errorMessage}>
          <FiAlertCircle />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className={styles.loadingState}>Loading billing details...</div>
      ) : (
        <form className={styles.form} onSubmit={handleSubmit}>
          <section className={styles.section}>
            <h3>Business identity</h3>
            <div className={styles.gridTwo}>
              <label className={styles.field} htmlFor="billing-company-name">
                <span>Company name</span>
                <input id="billing-company-name" value={form.company_name} onChange={updateField('company_name')} disabled={disabled} />
              </label>
              <label className={styles.field} htmlFor="billing-email">
                <span>Billing email</span>
                <input id="billing-email" type="email" value={form.billing_email} onChange={updateField('billing_email')} disabled={disabled} />
              </label>
            </div>
          </section>

          <section className={styles.section}>
            <h3>Billing address</h3>
            <div className={styles.gridTwo}>
              <label className={styles.field} htmlFor="billing-line1">
                <span>Address line 1</span>
                <input id="billing-line1" value={form.line1} onChange={updateField('line1')} disabled={disabled} />
              </label>
              <label className={styles.field} htmlFor="billing-line2">
                <span>Address line 2</span>
                <input id="billing-line2" value={form.line2} onChange={updateField('line2')} disabled={disabled} />
              </label>
              <label className={styles.field} htmlFor="billing-city">
                <span>City</span>
                <input id="billing-city" value={form.city} onChange={updateField('city')} disabled={disabled} />
              </label>
              <label className={styles.field} htmlFor="billing-state">
                <span>State / region</span>
                <input id="billing-state" value={form.state} onChange={updateField('state')} disabled={disabled} />
              </label>
              <label className={styles.field} htmlFor="billing-postal-code">
                <span>Postal code</span>
                <input id="billing-postal-code" value={form.postal_code} onChange={updateField('postal_code')} disabled={disabled} />
              </label>
              <label className={styles.field} htmlFor="billing-country">
                <span>Country</span>
                <select id="billing-country" value={form.country} onChange={updateField('country')} disabled={disabled}>
                  <option value="">Select country</option>
                  {BILLING_COUNTRY_OPTIONS.map((country) => (
                    <option key={country.code} value={country.code}>{country.label}</option>
                  ))}
                </select>
              </label>
            </div>
          </section>

          <section className={styles.section}>
            <h3>Tax ID</h3>
            <div className={styles.gridTwo}>
              <label className={styles.field} htmlFor="billing-tax-type">
                <span>Tax ID type</span>
                <select id="billing-tax-type" value={form.tax_id_type} onChange={updateField('tax_id_type')} disabled={disabled}>
                  <option value="">No tax ID</option>
                  {STRIPE_TAX_ID_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </label>
              <label className={styles.field} htmlFor="billing-tax-value">
                <span>Tax ID value</span>
                <input id="billing-tax-value" value={form.tax_id_value} onChange={updateField('tax_id_value')} disabled={disabled} />
              </label>
            </div>
            <p className={styles.helpText}>Tax ID type and value must be updated together.</p>
          </section>

          {validationError && <div className={styles.errorMessage}><FiAlertCircle /><span>{validationError}</span></div>}

          <div className={styles.footer}>
            {showSyncStatus && (
              <div className={styles.syncStatus}>
                <FiCheckCircle />
                <span>Sync status:</span>
                <span className={getSyncBadgeClass(syncStatus)}>{syncStatus}</span>
              </div>
            )}
            {canEdit && <Button type="submit" variant="primary" loading={saving} disabled={disabled}>Save billing details</Button>}
          </div>
        </form>
      )}
    </Card>
  );
}
